import { Map as MBMap } from '@/components/Map';
import { useMainContext } from '@/lib/context';
import { YEARS } from '@/lib/fixedTypes';
import { getMunicipalities } from '@/lib/getResults';
import { DataLevel, MapSettings, Result, Year } from '@/lib/model';
import { mergeToResults } from '@/utils/mergeToResults';
import center from '@turf/center';
import { AllGeoJSON } from '@turf/helpers';
import { MapLayerMouseEvent } from 'mapbox-gl';
import * as React from 'react';
import { Layer, LayerProps, Source, useMap } from 'react-map-gl';
import './index.scss';
import { Popup } from './Popup';

// Default values
// const sourceId = 'aurelianv.Mappa_Reg_LazioLombardia_tileset';
// const sourceUrl = 'mapbox://aurelianv.Mappa_Reg_LazioLombardia_tileset';

/**
 * Nuovo layer con tutti i comuni italiani
 */
const SOURCE_ID_MAP_ALL = 'aurelianv.Mappa_Reg_Piemonte_tileset';
const SOURCE_URL_MAP_ALL = 'mapbox://aurelianv.Mappa_Reg_Piemonte_tileset';

export const Map: React.FC<MapSettings> = ({
  layerPropId,
  sourceLayer,
  zoom,
}) => {
  const {
    setResults,
    results,
    handleResult,
    dataLevel,
    lastResult,
    setComuneIsLoading,
    selectedCandidate,
    selectedResults,
    selectedYear,
    isTurnoutOpen,
    dynamicTurnout,
  } = useMainContext();

  const [cursor, setCursor] = React.useState<string>('auto');
  const [point, setPoint] = React.useState({ x: 0, y: 0 });
  const [popupResult, setPopupResult] = React.useState<Result | null>(null);
  const { electionsMap: map } = useMap();
  const [isCoordinateSet, setIsCoordinateSet] = React.useState(false);

  //Timeout per il debouncetime
  const [timeoutFetchMunicipalities, setTimeoutFetchMunicipalities] =
    React.useState(
      null as null | { region: string; province: string; timeout: any }
    );

  const layersMap = new globalThis.Map<string, Result>();
  results.forEach((el: any) => {
    layersMap.set(el.mapLayer.layerId + '', el);
  });

  const interactiveLayerIds = Array.from(layersMap.keys());

  React.useEffect(() => {
    const { coordinates } = lastResult?.mapLayer;
    const isItSet = !!coordinates.length;
    setIsCoordinateSet(isItSet);
  }, [lastResult]);

  React.useEffect(() => {
    // set map center to given coordinates
    const goTo = ([lng, lat]: number[]) => {
      // override for Basilicata
      // lat = 42.2429436;
      // lng = 13.923352;
      map?.setCenter({
        lat,
        lng,
      });
    };

    const onLayerLoad = () => {
      const layerId = `${lastResult.mapLayer.layerId}`;

      // check if layer has rendered before proceeding
      const isLayerRendered = map?.getLayer(layerId);
      if (!isLayerRendered) {
        return;
      }

      let layers = map?.queryRenderedFeatures(undefined, {
        layers: [layerId],
      });

      if (layers?.length === 0) {
        // Juan please check this
        let layersAll = map?.querySourceFeatures('composite', {
          sourceLayer: '2024_Comuni_Piemonte',
        });
        layers = layersAll?.filter(l => l.id === +layerId);
      }

      if (!layers?.length) {
        return;
      }
      const { coordinates } = center(layers[0] as AllGeoJSON).geometry;

      // go to coordinate
      goTo(coordinates);

      // setting flag to true after finding coordinate
      setIsCoordinateSet(true);
      setComuneIsLoading(false);
    };

    const { coordinates } = lastResult?.mapLayer;

    if (!coordinates.length) {
      // these following listeners work to understand
      // when map has fully loaded
      if (isCoordinateSet) {
        map?.getMap().off('idle', onLayerLoad);
      } else {
        map?.once('idle', onLayerLoad);
      }

      return;
    }

    setIsCoordinateSet(true);

    if (lastResult?.level === DataLevel.REGIONE) {
      // recenter only if at region level, for province and comune no center of the map on click
      goTo(coordinates);
    }
  }, [
    map,
    lastResult,
    isCoordinateSet,
    setIsCoordinateSet,
    setComuneIsLoading,
  ]);

  React.useEffect(() => {
    map?.setZoom(zoom);
  }, [map, zoom]);

  /**
   * Show popup on mouse move
   * @param e
   */
  const onMouseMove = async (e: MapLayerMouseEvent) => {
    const feature = e.features?.length && e.features[0];
    if (!feature) {
      // close popup
      setPopupResult(null);
      return;
    }

    if (point.x === e.point.x && point.y === e.point.y) {
      // console.warn('Map ::: onMouseMove ::: le coordinate non sono cambiate');
      return;
    }

    const layerId = feature.id + '';

    const result = (layersMap.get(layerId) || null) as Result;

    // console.log(dataLevel, DataLevel.COMUNE, DataLevel.PROVINCIA);

    //Controllo se viene effettuata una chiamata con gli stessi dati
    if (
      result.idCodes.codReg === timeoutFetchMunicipalities?.region &&
      result.idCodes.codProv === timeoutFetchMunicipalities?.province
    ) {
      // console.log('chiamata in corso...');
      // open popup with info
      setPopupResult(result);
      return;
    }

    if (result.coalitions) {
      // console.log('point', e.point)
      setPoint(e.point);
      setPopupResult(result);
      return;
    }

    if (timeoutFetchMunicipalities?.timeout) {
      clearTimeout(timeoutFetchMunicipalities.timeout);
    }

    const timeout = setTimeout(async () => {
      // position popup
      setPoint(e.point);

      // TODO la chiamata deve essere fatta su REG-PROV viene restituita tutta la lista dei comuni, man mano che l'utente si sposta sulle province prendo carico altri comuni
      // richiamare questo FILE https://2023-real-time-data.s3.eu-central-1.amazonaws.com/2022/0703057.json

      try {
        const resx = await getMunicipalities(
          result.idCodes.codReg!,
          result.idCodes.codProv!,
          Year.Y2022,
          dataLevel,
          dynamicTurnout
        );

        const { results: newResults, found: popoupInfo } = mergeToResults(
          results,
          resx,
          result
        );
        setResults(newResults);

        // open popup with info
        setPopupResult(popoupInfo);
      } catch (error) {
        console.error(error);
      }
    }, 1000);

    const newTimeout = {
      timeout: timeout as any,
      region: result.idCodes.codReg!,
      province: result.idCodes.codProv!,
    };

    setTimeoutFetchMunicipalities(newTimeout);
  };

  // return layer when clicked with current coordinates
  const onClick = async (e: MapLayerMouseEvent) => {
    // avoid clicking on layer if in inner selected candidate
    if (selectedCandidate) {
      return;
    }

    const feature = e.features?.length && e.features[0];
    // I set popupResult again, to avoid the first tap not working on Android
    let popResult;
    if (feature) {
      const layerId = feature.id + '';
      popResult = (layersMap.get(layerId) || null) as Result;
      setPopupResult(popResult);
    }
    if (!popResult || !feature) {
      return;
    }

    // zoom and center to new coordinates
    const { coordinates: newCoordinates } = center(
      feature as AllGeoJSON
    ).geometry;

    while (Math.abs(e.lngLat.lng - newCoordinates[0]) > 180) {
      newCoordinates[0] += e.lngLat.lng > newCoordinates[0] ? 360 : -360;
    }

    // add result to left pannel
    handleResult(
      {
        ...popResult,
        mapLayer: {
          ...popResult.mapLayer,
          coordinates: newCoordinates,
        },
      },
      false
    );

    // raise "electio:navigate" event for sole
    const selectedYearObj = YEARS.find(y => y.value === selectedYear);
    let nomeProvincia = selectedResults[1]?.placeName || '';
    let nomeComune = selectedResults[2]?.placeName || '';

    if (popResult.level === DataLevel.PROVINCIA) {
      nomeProvincia = popResult.placeName;
    } else if (popResult.level === DataLevel.COMUNE) {
      nomeComune = popResult.placeName;
    }

    const event = new CustomEvent('electio:navigate', {
      detail: {
        nomeRegione: selectedResults[0]?.placeName || '',
        nomeProvincia,
        nomeComune,
        anno: selectedYearObj?.label,
      },
    });

    document.dispatchEvent(event);
  };

  // set cursor styles
  const onMouseEnter = () => {
    setCursor('pointer');
  };

  const onMouseLeave = () => {
    setCursor('auto');
    // close popup
    setPopupResult(null);
  };

  const areResultsValid = results.length > 0 && results[0].level === dataLevel;

  //
  //
  //

  React.useEffect(() => {
    // TODO DEFAULT COORDINATES
    if (lastResult?.mapLayer.coordinates?.length === 0) {
      lastResult.mapLayer.coordinates = [16.2255482, 40.4031845];
    }
  }, []);

  if (
    dataLevel === DataLevel.PROVINCIA &&
    lastResult?.mapLayer.coordinates?.length === 0
  ) {
    console.log('imposto il centro mappa');
    lastResult.mapLayer.coordinates = [7.7834352, 45.1214852]; //Per centrare l'italia
  }

  return (
    <div className="Map">
      <MBMap
        id="electionsMap"
        initialViewState={{
          latitude: lastResult?.mapLayer.coordinates[1],
          longitude: lastResult?.mapLayer.coordinates[0],
          zoom,
        }}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onMouseMove={onMouseMove}
        interactiveLayerIds={interactiveLayerIds}
        cursor={cursor}
        cooperativeGestures={true}
        maxPitch={0}
      >
        <Source id={SOURCE_ID_MAP_ALL} type="vector" url={SOURCE_URL_MAP_ALL}>
          {areResultsValid &&
            results.map((result, i) => {
              const mapLayerId = Number(result?.mapLayer?.layerId) ?? null;

              if (mapLayerId === null) {
                return null;
              }

              // const isSelected =
              //   lastResult?.mapLayer.layerId === result.mapLayer.layerId ||
              //   lastResult?.level === DataLevel.MAIN;

              const isSelected =
                (lastResult?.level === DataLevel.REGIONE &&
                  lastResult.idCodes.codReg === result.idCodes.codReg) ||
                (lastResult?.level === DataLevel.PROVINCIA &&
                  lastResult.idCodes.codReg === result.idCodes.codReg &&
                  lastResult.idCodes.codProv === result.idCodes.codProv) ||
                ((lastResult?.level === DataLevel.COMUNE ||
                  result.level === DataLevel.COMUNE) &&
                  lastResult.idCodes.codReg === result.idCodes.codReg &&
                  lastResult.idCodes.codProv === result.idCodes.codProv &&
                  lastResult.idCodes.codCom === result.idCodes.codCom);

              const opacity =
                !selectedCandidate && !isTurnoutOpen
                  ? isSelected
                    ? 1
                    : 0.6
                  : result.mapLayer.opacity;

              const layerProperties: LayerProps = {
                id: `${result.mapLayer.layerId}`,
                'source-layer': sourceLayer,
                minzoom: 6, // to improve speed loading
                maxzoom: 10, // to improve speed loading
                type: 'fill',
                filter: ['==', layerPropId, mapLayerId],
                paint: {
                  'fill-color': result.mapLayer.fillColor,
                  'fill-opacity': opacity,
                  'fill-outline-color': 'rgba(0, 0, 0, 0.3)',
                },
                beforeId: 'waterway-shadow',
              };

              return (
                <Layer
                  {...layerProperties}
                  key={`${layerPropId}_${result.mapLayer.layerId}_${i}`}
                />
              );
            })}
        </Source>
        {popupResult && (
          <Popup
            data={popupResult}
            x={point.x || 0}
            y={point.y || 0}
            offset={15}
            selectedCandidate={selectedCandidate}
            selectedYear={selectedYear}
            isTurnoutOpen={isTurnoutOpen}
            dynamicTurnout={dynamicTurnout}
            dataLevel={dataLevel}
            lastResult={lastResult}
          />
        )}
      </MBMap>
    </div>
  );
};
